import { UI } from '../../UI.ts'
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js'
import { type AnimationClip, Scene, type SkinnedMesh, type Object3D } from 'three'
import { type DownloadSettings, ExportContents } from './DownloadSettings.ts'
import { ExportBoneNamingService } from './ExportBoneNamingService.ts'
import { HandoffUploader } from './HandoffUploader.ts'

// Note: EventTarget is a built-in interface and do not need to import it
export class StepExportToFile extends EventTarget {
  private readonly ui: UI = UI.getInstance()
  private animation_clips_to_export: AnimationClip[] = []

  public set_animation_clips_to_export (all_animations_clips: AnimationClip[], animation_checkboxes: number[]): void {
    this.animation_clips_to_export = []
    animation_checkboxes.forEach((indx) => {
      const original_clip: AnimationClip = all_animations_clips[indx]
      const cloned_clip: AnimationClip = original_clip.clone()
      this.animation_clips_to_export.push(cloned_clip)
    })
  }

  public export (
    skinned_meshes: SkinnedMesh[], 
    filename: string = 'exported_model',
    download_settings: DownloadSettings): void {

    if (this.animation_clips_to_export.length === 0) {
      console.log('ERROR: No animation clips added to export')
      return
    }

    const export_scene = new Scene()

    const export_clips = this.animation_clips_to_export.map((clip) => {
      const cloned_clip = clip.clone()
      // Make action names consistent by replacing any whitespace with underscores
      cloned_clip.name = cloned_clip.name.replace(/\s+/g, '_')
      return cloned_clip
    })

    // Apply bone naming before re-parenting: the service traverses each skinned mesh to
    // find its bones, which are still children of the mesh at this point.
    const restore_bone_names = ExportBoneNamingService.apply_download_settings(
      skinned_meshes,
      export_clips,
      download_settings.bone_naming_structure()
    )

    // "Skeleton only" exports just the bone hierarchy (the root bone subtree that hangs off
    // each skinned mesh) instead of the full skinned mesh, which excludes the mesh geometry.
    // The animation clips still target the bone names, so the selected animations continue
    // to work against the exported skeleton.
    const skeleton_only = download_settings.export_contents() === ExportContents.Skeleton
    const objects_to_export: Object3D[] = skeleton_only
      ? Array.from(new Set(skinned_meshes.map((mesh) => mesh.skeleton.bones[0])))
      : skinned_meshes

    // When exporting to a file, we need to temporarily move the exported objects to a new
    // scene. An object can only be part of one scene at a time, so we must move it back to
    // its original parent after exporting. For a skeleton-only export, the root bone's
    // original parent is its skinned mesh.
    const original_parents = new Map<Object3D, Object3D | null>()

    objects_to_export.forEach((object_to_export) => {
      // Save the original parent
      original_parents.set(object_to_export, object_to_export.parent)
      export_scene.add(object_to_export)
    })

    console.log('SKINNED MESH DATA TO EXPORT:', skinned_meshes)
    console.log('animations to export', export_clips)

    this.export_glb(export_scene, export_clips, filename, download_settings.bone_naming_structure())
      .then(() => {
        // Move the exported objects back to their original parents
        objects_to_export.forEach((exported_object) => {
          const original_par = original_parents.get(exported_object)
          if (original_par != null) {
            original_par.add(exported_object)
          } else {
            // If there was no original parent, remove it from the scene
            export_scene.remove(exported_object)
            console.log('ERROR: No original parent found for exported object when exporting and re-parenting to original scene')
          }
        })

        restore_bone_names()
      })
      .catch((error) => {
        console.log('Error exporting GLB:', error)

        restore_bone_names()
      })
  }

  public async export_glb (
    exported_scene: Scene,
    animations_to_export: AnimationClip[],
    file_name: string,
    bone_naming: string = 'default'): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      const gltf_exporter = new GLTFExporter()

      const export_options = {
        binary: true,
        onlyVisible: false,
        embedImages: true,
        animations: animations_to_export
      }

      gltf_exporter.parse(
        exported_scene,
        (result: ArrayBuffer) => {
          // Handle the result of the export
          if (result !== null) {
            this.save_array_buffer(result, `${file_name}.glb`)
            // Round-trip back to the host game when we were deep-linked with an upload
            // ticket. Fire-and-forget: the local download above already succeeded, so a
            // failed upload never costs the user their file.
            void HandoffUploader.upload(result, bone_naming)
            resolve() // Resolve the promise when the export is complete
          } else {
            console.log('ERROR: result is not an instance of ArrayBuffer')
            reject(new Error('Export result is not an ArrayBuffer'))
          }
        },
        (error: any) => {
          console.log('An error happened during parsing', error)
          reject(error) // Reject the promise if an error occurs
        },
        export_options
      )
    })
  }

  private save_file (blob: Blob, filename: string): void {
    if (this.ui.dom_export_button_hidden_link != null) {
      this.ui.dom_export_button_hidden_link.href = URL.createObjectURL(blob)
      this.ui.dom_export_button_hidden_link.download = filename
      this.ui.dom_export_button_hidden_link.click()
    } else {
      console.log('ERROR: dom_export_button_hidden_link is null')
    }
  }

  // used for GLB to turn content into a byte array for saving
  private save_array_buffer (buffer: ArrayBuffer, filename: string): void {
    this.save_file(new Blob([buffer], { type: 'application/octet-stream' }), filename)
  }
}
